export type DataType =
  | "ID"
  | "Text"
  | "Boolean"
  | "Number"
  | "State"
  | "Date"
  | "Duration"
  | "Tag"
  | "User"
  | "Account"
  | "Recipients"
  | "Resource";

const fieldList = [
  {
    fieldName: "requestId",
    dataType: "ID",
    isSortable: true,
  },
  {
    fieldName: "resource",
    dataType: "Text",
    isSortable: false,
  },
  {
    fieldName: "scope",
    dataType: "Text",
    isSortable: false,
  },
  {
    fieldName: "action",
    dataType: "Text",
    isSortable: true,
  },
  {
    fieldName: "target",
    dataType: "Text",
    isSortable: false,
  },
  {
    fieldName: "requestedBy",
    dataType: "User",
    isSortable: false,
  },
  {
    fieldName: "state",
    dataType: "State",
    isSortable: true,
  },
  {
    fieldName: "expiresAt",
    dataType: "Date",
    isSortable: true,
  },
  {
    fieldName: "expiration",
    dataType: "Date",
    isSortable: true,
  },
  {
    fieldName: "createdAt",
    dataType: "Date",
    isSortable: true,
  },
  {
    fieldName: "recipients",
    dataType: "Recipients",
    isSortable: false,
  },
  {
    fieldName: "requestReason",
    dataType: "Text",
    isSortable: false,
  },
  {
    fieldName: "processedBy",
    dataType: "User",
    isSortable: false,
  },
] as const;

export type FieldName = (typeof fieldList)[number]["fieldName"];

export type Fields = {
  [FieldName in (typeof fieldList)[number]["fieldName"]]: Extract<
    (typeof fieldList)[number],
    { fieldName: FieldName }
  >;
};

export type Field = Fields[keyof Fields];

export type ResourceField<ResourceType extends object> = {
  fieldName: keyof ResourceType;
} & Field;

export type FieldLabels<ResourceType extends object> = {
  [FieldName in keyof ResourceType &
    (typeof fieldList)[number]["fieldName"]]: string;
};

const _fields = {} as Record<keyof Fields, Field>;
for (const field of fieldList) {
  _fields[field.fieldName] = field;
}
export const fields = _fields as Fields;

export function isKnownField(fieldName: string): fieldName is keyof Fields {
  return fieldName in fields;
}

export const defaultFieldLabels = {
  requestId: "ID",
  resource: "Resource",
  scope: "Scope",
  action: "Action",
  target: "Target",
  requestedBy: "Requested By",
  state: "State",
  expiresAt: "Request Expiration",
  expiration: "Access Expiration",
  createdAt: "Created",
  recipients: "Approvers",
  requestReason: "Reason",
  processedBy: "Processed By",
} as const;

export function getDefaultSortOrder(field: Field): "asc" | "desc" {
  switch (field.dataType) {
    case "Date":
      return "desc";
    default:
      return "asc";
  }
}

export const defaultSortField = fields.createdAt;
